import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing16, Spacing32 } from 'autogen/design-tokens/tokens';
import type { ProductSubscriptionPriceInfoViewModel, SubscriptionPriceInfo } from 'autogen/swagger/all';
import {
    Description,
    DescriptionIntroPrice,
    Heading
} from 'autogen/translation-keys/trans-website-basket-subscription-priceinfo';
import { ReadMore } from 'autogen/translation-keys/trans-website-shared-buttons';
import InlineButton from 'components/generic/InlineButton/InlineButton';
import { Typography } from 'components/generic/typography';
import { useQuickView } from 'components/quickView';
import QuickView from 'components/quickView/quickView';
import { format } from 'modules/helpers/strings';
import { breakpoint, sizes } from 'variables';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${Spacing16} ${Spacing16};

    ${breakpoint.up(sizes.lg)} {
        padding: 0 ${Spacing32} ${Spacing32};
    }
`;

const Headline = styled(Typography.Headline)`
    margin-bottom: ${Spacing32};
`;

export default function PriceExplainer({
    subscriptionPriceInfo,
    discountPercentage
}: {
    subscriptionPriceInfo: ProductSubscriptionPriceInfoViewModel;
    discountPercentage: SubscriptionPriceInfo['discountPercentage'];
}): JSX.Element {
    const { currentPrice, subscriptionSubsequentlyPrice, showSubscriptionIntroPrice } = subscriptionPriceInfo;
    const { t } = useTranslation();
    const { toggle } = useQuickView('SubscriptionPriceExplainer');
    const calculatedPercentage = `${discountPercentage * 100}%`;
    const paragraph = showSubscriptionIntroPrice
        ? format(t(DescriptionIntroPrice))(subscriptionSubsequentlyPrice, calculatedPercentage, currentPrice)
        : format(t(Description))(subscriptionSubsequentlyPrice, calculatedPercentage, currentPrice);

    return (
        <>
            <InlineButton fontWeight="semibold" onClick={() => toggle(true)}>
                {t(ReadMore)}
            </InlineButton>

            <QuickView quickViewId="SubscriptionPriceExplainer">
                <Container>
                    <Headline size="M">{t(Heading)}</Headline>
                    <Typography.Body dangerouslySetInnerHTML={{ __html: paragraph }} />
                </Container>
            </QuickView>
        </>
    );
}
